/**
 * SHEET NOTES BAND (docs/bim-plan/28 round 10): a sheet type's firm notes in the title strip's
 * notes band — floor plan notes on A10X, roof notes on A141, section notes on A20X … — replacing
 * the Documint "PDF Insert" stickers. The blocks are firm data (Supabase `sheet_notes`, edited in
 * Templates -> Sheet Notes); the Modeler loads them on open into this module
 * (`setSheetNotesList`), the G002 / abbreviations pattern.
 *
 * A block prints on a sheet when its sheet type is the sheet's (`SheetRecord.airtableTemplateId`)
 * and every fact in its `appliesWhen` holds for the model ("pool", "!renovation", "newport,
 * fireZone"). The facts are read from the model itself (a pool element, a fireplace, stairs, an
 * existing phase) and from the mirrored project record (state, jurisdiction, fire zone) — one
 * plain vocabulary, listed in NOTE_FACTS so the Templates editor can offer it.
 */
import { resolveAllElements, listPhases, listLevels, getTitleSheetData, getProjectInfo, isNewportBeach, isLagunaBeach } from '@aj/bim-kernel'

let NOTES = []
/** the firm list, every sheet type: [{ id, templateId, title, body, appliesWhen, active }] */
export function setSheetNotesList(rows) { NOTES = Array.isArray(rows) ? rows.filter((r) => r && r.active !== false) : [] }
export function getSheetNotesList() { return NOTES }

/** the facts a block can depend on — key → plain description (the editor lists these) */
export const NOTE_FACTS = {
  california: 'Project is in California',
  newport: 'Newport Beach project',
  laguna: 'Laguna Beach project',
  fireZone: 'In a fire hazard severity zone',
  renovation: 'Model has existing construction (a remodel)',
  newConstruction: 'Model has new construction',
  pool: 'Model has a pool or spa',
  fireplace: 'Model has a fireplace or wood stove',
  stair: 'Model has stairs',
  railing: 'Model has railings or guards',
  garage: 'Model has a garage',
  multiStory: 'Building has more than one story',
}

const FIREPLACE_KINDS = new Set(['fireplaceLinear', 'fireplaceWood', 'woodStove'])

/** the model's facts, by NOTE_FACTS key */
export function noteFactsFor(model) {
  const els = resolveAllElements(model)
  const phases = listPhases(model)
  const first = phases[0]?.id
  const d = getTitleSheetData(model) || {}
  const state = String(d?.project?.state || '').trim()
  const fhsz = String(d?.parcel?.fhsz || '').trim()
  const garageLevel = listLevels(model).some((l) => /garage/i.test(l.name))
  const storyIds = new Set(els.filter((e) => e.category === 'wall').map((e) => e.levelId))
  // a remodel: the model has elements created in its first (existing conditions) phase — the
  // phase sits on the element itself, and an element with none was created in the first phase
  const createdIn = (e) => e.phaseCreated ?? first
  const existing = phases.length > 1 && els.some((e) => createdIn(e) === first)
  return {
    california: /^(ca|california)$/i.test(state) || /\bCA\b/.test(getProjectInfo(model).cityStateZip || ''),
    newport: isNewportBeach(model),
    laguna: isLagunaBeach(model),
    fireZone: !!fhsz && !/^not in/i.test(fhsz),
    renovation: existing,
    newConstruction: els.some((e) => createdIn(e) !== first) || phases.length <= 1,
    pool: els.some((e) => e.category === 'pool'),
    fireplace: els.some((e) => e.category === 'object' && FIREPLACE_KINDS.has(String(e.typeParams?.objectKind || ''))),
    stair: els.some((e) => e.category === 'stair'),
    railing: els.some((e) => e.category === 'railing'),
    garage: garageLevel || els.some((e) => e.category === 'door' && /garage|sectional|overhead/i.test(String(e.typeParams?.operation || ''))),
    multiStory: storyIds.size > 1,
  }
}

/** does `appliesWhen` ("pool", "!renovation", "newport,fireZone") hold for these facts? */
export function noteApplies(appliesWhen, facts) {
  const keys = String(appliesWhen || '').split(/[,\s]+/).map((k) => k.trim()).filter(Boolean)
  return keys.every((k) => (k.startsWith('!') ? !facts[k.slice(1)] : !!facts[k]))
}

// the project's consultant disciplines (from the Cortex drawing set's consultant sheets), for
// the "FOR ADDITIONAL INFORMATION SEE {{disciplines}} DRAWINGS." line — set by the Modeler on open
let DISCIPLINES = []
export const CONSULTANT_DISCIPLINES = ['Civil', 'Landscape', 'Structural', 'Mechanical', 'Electrical', 'Plumbing']
export function setConsultantDisciplines(list) {
  const have = new Set((list || []).map((d) => String(d || '').trim().toLowerCase()))
  DISCIPLINES = CONSULTANT_DISCIPLINES.filter((d) => have.has(d.toLowerCase()))
}
/** "A", "A AND B", "A, B, AND C" */
function joinList(xs) {
  if (xs.length <= 2) return xs.join(' AND ')
  return xs.slice(0, -1).join(', ') + ', AND ' + xs[xs.length - 1]
}

/**
 * The strip's note blocks for a sheet ([{ type: 'h1' | 'bullet' | 'sub', text, numbered, depth }]
 * — the layoutNoteBlocks vocabulary), or [] when its type has none.
 *
 * One note per line of a block's body. A line indented with spaces is a sub-item of the note above
 * it (two spaces = "A.", four = "a."), printed indented and outside the numbering. A line with
 * {{disciplines}} names the project's consultant drawings and is left out when there are none.
 */
export function sheetNoteBlocks(model, sheet) {
  const tid = sheet?.airtableTemplateId
  if (!tid || !NOTES.length) return []
  const mine = NOTES.filter((n) => n.templateId === tid)
  if (!mine.length) return []
  const facts = noteFactsFor(model)
  const out = []
  for (const n of mine) {
    if (!noteApplies(n.appliesWhen, facts)) continue
    const lines = []
    for (const raw of String(n.body || '').split('\n')) {
      if (!raw.trim()) continue
      let text = raw.replace(/\s+$/, '')
      if (text.includes('{{disciplines}}')) {
        if (!DISCIPLINES.length) continue
        text = text.replace(/\{\{disciplines\}\}/g, joinList(DISCIPLINES.map((d) => d.toUpperCase())))
      }
      const depth = Math.min(2, Math.floor((text.length - text.trimStart().length) / 2))
      // capitals throughout, except a sub-item's own letter ("a." stays "a.")
      const m = depth ? /^([A-Za-z0-9]{1,3}[.,)])\s+(.*)$/.exec(text.trim()) : null
      lines.push({ depth, text: m ? `${m[1]} ${m[2].toUpperCase()}` : text.trim().toUpperCase() })
    }
    if (!lines.length && !n.title) continue
    if (n.title) out.push({ type: 'h1', text: String(n.title).toUpperCase() })
    for (const l of lines) out.push(l.depth ? { type: 'sub', depth: l.depth, text: l.text } : { type: 'bullet', numbered: true, text: l.text })
  }
  return out
}
